import React, { useState } from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import StoreIcon from '@material-ui/icons/Store';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import TablePagination from '@material-ui/core/TablePagination';

import MakerText from '../labels/MakerText';
import MakerSelect from '../inputs/MakerSelect';
import LensText from '../labels/LensText';
import LensSelect from '../inputs/LensSelect';
import { StockOrder, StockLensData, StockOrderStatusEnum, Status } from 'openapi';
import CommonContainer from "../../containers/Common";
import StockOrderContainer from "../../containers/StockOrder";
import StatusColumn from '../labels/StatusColumn';
import DateTimePicker from '../labels/DateTimePicker';
import StatusPanel from '../panels/StatusPanel';
import StockInputField from '../inputs/StockInputField';
import ReloadButton from '../buttons/ReloadButton';
import { Grid } from '@material-ui/core';

interface Props {
  isLoading: boolean,
  date: Date,
  onSelectedDateChange: (date: Date | null) => void,
  onOrder: (stockOrders: StockOrder[]) => void,
  onReload: () => void,
  onRemarks: (stockOrders: StockOrder[]) => void,
}

export default ((props) => {

  const commonContainer = CommonContainer.useContainer();
  const stockOrderContainer = StockOrderContainer.useContainer();

  const [selectedStockOrders, setSelectedStockOrders] = useState<StockOrder[]>([]);
  const [open, setOpen] = useState(false);
  const [statusList, setStatusList] = useState<Status[]>([]);

  const unselectStockOrder = (meganetLensOrderId: number) => {
    setSelectedStockOrders(stockOrders => {
      var index = stockOrders.findIndex(stockOrder => stockOrder.meganetLensOrderId === meganetLensOrderId);
      if (index === -1) {
        return stockOrders;
      }

      return [...stockOrders.slice(0, index), ...stockOrders.slice(index + 1)];
    });
  };

  const handleStatus = async (orderId: number) => {
    // Show panel
    setOpen(true);

    // get status List
    var results = await stockOrderContainer.getStatusList(orderId);
    setStatusList(results ? results : []);
  }

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <StatusPanel handleClickOpen={() => handleStatus} handleClose={handleClose} open={open} statusList={statusList} />
      <MaterialTable
        title="Stock Order"
        isLoading={props.isLoading}
        components={{
          Toolbar: innerProps => (
            <div>
              <MTableToolbar {...innerProps} />
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                <DateTimePicker date={props.date} onSelectedDateChange={props.onSelectedDateChange} />
                <div>
                  <ReloadButton onReload={props.onReload} />
                </div>
              </Grid>
            </div>
          ),
          Pagination: props => (
            <>
              <TablePagination {...props} />
            </>
          ),
        }}
        columns={[
          {
            title: 'Status', field: 'orderStatus', editable: 'never',
            render: data => <StatusColumn status={data ? data.status : StockOrderStatusEnum.Waiting} orderId={data ? data.meganetLensOrderId : -1} handleStatus={handleStatus} />,
          },
          { title: 'KarteNo', field: 'karteNo', type: 'numeric', editable: 'never', filtering: false },
          { title: 'Customer', field: 'customerName', editable: 'never' },
          {
            title: 'Maker', field: 'meganetLensMasterId', filtering: false,
            render: data => <MakerText meganetLensMasterId={data.meganetLensMasterId} />,
            editComponent: props => <MakerSelect
              makerCode={(() => {
                const maker = commonContainer.getMaker(props.value);
                return maker ? maker.makerCode : undefined;
              })()}
              onChange={(makerCode: string) => {
                const lenses = commonContainer.getLensesByMakerCode(makerCode);
                props.onChange(lenses ? lenses[0].meganetLensMasterId : undefined);
              }} />,
            customFilterAndSearch: (filter, rowData) => {
              if (typeof filter === 'string') {
                const maker = commonContainer.getMaker(rowData.meganetLensMasterId);
                return maker ? maker.makerName.includes(filter) : false;
              }

              return true;
            },
          },
          {
            title: 'Lens', field: 'meganetLensMasterId', filtering: false,
            render: data => <LensText meganetLensMasterId={data.meganetLensMasterId} />,
            editComponent: props => <LensSelect meganetLensMasterId={props.value} onChange={props.onChange} />,
            customFilterAndSearch: (filter, rowData) => {
              if (typeof filter === 'string') {
                const lens = commonContainer.getLens(rowData.meganetLensMasterId);
                return lens ? lens.lensName.includes(filter) : false;
              }

              return true;
            },
          },
          {
            title: 'Sph', field: 'sph', initialEditValue: 0,
            render: data => <>{data.sph.toFixed(2)}</>,
            editComponent: props => <StockInputField value={props.value} type='number' placeholder='Sph' onChange={props.onChange} />
          },
          {
            title: 'Cyl', field: 'cyl', initialEditValue: 0,
            render: data => <>{data.cyl.toFixed(2)}</>,
            editComponent: props => <StockInputField value={props.value} type='number' placeholder='Cyl' onChange={props.onChange} />
          },
          {
            title: 'Remarks', field: 'remarks', initialEditValue: '',
            render: data => <>{data.remarks}</>,
            editComponent: props => props.rowData.canEditRemarks || props.rowData.canEditRemarks === undefined ? <StockInputField value={props.value} type='text' placeholder='remarks' onChange={props.onChange} hasKarteNo={props.rowData.karteNo !== undefined}/> : <>{props.value}</>,
          },
        ]}
        data={stockOrderContainer.stockOrders}
        options={{
          filtering: false,
          selection: true,
          selectionProps: (data: StockOrder) => {
            if (data.status !== StockOrderStatusEnum.Waiting) {
              return {
                style: { visibility: 'hidden' },
              }
            }
          },
          actionsColumnIndex: -1,
          pageSize: 50,
          pageSizeOptions: [],
        }}
        localization={{
          toolbar: {
            nRowsSelected: `${selectedStockOrders.length} row(s) selected`,
          },
        }}
        onSelectionChange={(rows) => {
          setSelectedStockOrders(rows.filter(row => row.status === StockOrderStatusEnum.Waiting));
        }}
        actions={[
          {
            tooltip: 'Remarksの内容がSTOCKになります',
            icon: () => <StoreIcon />,
            onClick: async (evt, data: any) => {
              await props.onRemarks(selectedStockOrders);
            }
          },
          {
            tooltip: 'Order',
            icon: () => <CloudUploadIcon />,
            onClick: async (evt, data: any) => {
              await props.onOrder(selectedStockOrders);
              setSelectedStockOrders([]);
            }
          },
        ]}
        editable={{
          isEditable: (rowData) => { return true },
          isDeletable: (rowData) => { return true },
          onRowAdd: async (newData) => {
            return new Promise<void>((resolve, reject) => {
              setTimeout( async () => {
                if (newData.remarks && (!newData.remarks.match(/^[ァ-ンヴーｧ-ﾝﾞﾟ\-0-9a-zA-Z]*$/) || (newData.karteNo !== undefined ? newData.remarks.length > 10 : newData.remarks.length > 20))) {
                  reject();
                  return;
                }

                newData.recordedDate = props.date;
                await stockOrderContainer.create(newData as StockLensData);
                resolve();
              }, 100);
            });
          },
          onRowUpdate: async (newData) => {
            return new Promise<void>((resolve, reject) => {
              setTimeout( async () => {
                if (newData.remarks && (!newData.remarks.match(/^[ァ-ンヴーｧ-ﾝﾞﾟ\-0-9a-zA-Z]*$/) || (newData.karteNo !== undefined ? newData.remarks.length > 10 : newData.remarks.length > 20))) {
                  reject();
                  return;
                }

                newData.recordedDate = props.date;
                await stockOrderContainer.update(newData as StockOrder);
                unselectStockOrder(newData.meganetLensOrderId);
                resolve();
              }, 100);
            });
          },
          onRowDelete: async (oldData) => {
            await stockOrderContainer.remove(oldData.meganetLensOrderId);
            unselectStockOrder(oldData.meganetLensOrderId);
          },
        }}
      />
    </>
  );
}) as React.FC<Props>;
